import React, { useState, useEffect } from "react";
import { toastr } from 'react-redux-toastr';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Nav,
  NavItem,
  NavLink,
  Button
} from "reactstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import Header from "../../components/Headers/Header.jsx";
// import FinancialChatbot from "../../components/FinancialChatbot";
import general_fetch from "../../utils/fetch.js";
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const FinancialReports = ({ user_details, set_page_loader }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [daysBack, setDaysBack] = useState(90);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCashflowDate, setSelectedCashflowDate] = useState(null);
  const [generatingCashflow, setGeneratingCashflow] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  // const [chatbotVisible, setChatbotVisible] = useState(false);

  // Chart options for ARR
  const arrChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          padding: 12,
          usePointStyle: true,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        cornerRadius: 6,
        displayColors: true,
        callbacks: {
          label: function(context) {
            return context.dataset.label + ': ' + formatCurrency(context.parsed.y);
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 10
          },
          maxTicksLimit: 8
        }
      },
      y: {
        beginAtZero: false,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 10
          },
          maxTicksLimit: 6,
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      }
    },
    elements: {
      point: {
        radius: 2,
        hoverRadius: 5
      },
      line: {
        borderWidth: 2
      }
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10
      }
    }
  };

  // Chart options for Cash Flow
  const cashflowChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          padding: 12,
          usePointStyle: true,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        cornerRadius: 6,
        displayColors: true,
        callbacks: {
          label: function(context) {
            return context.dataset.label + ': ' + formatCurrency(context.parsed.y);
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 10
          },
          maxTicksLimit: 8
        }
      },
      y: {
        beginAtZero: false,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 10
          },
          maxTicksLimit: 6,
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      }
    },
    elements: {
      point: {
        radius: 2,
        hoverRadius: 5
      },
      line: {
        borderWidth: 2
      }
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10
      }
    }
  };

  // Format currency in INR
  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return '₹' + (amount / 10000000).toFixed(2) + 'Cr';
    } else if (amount >= 100000) {
      return '₹' + (amount / 100000).toFixed(1) + 'L';
    } else if (amount >= 1000) {
      return '₹' + (amount / 1000).toFixed(0) + 'K';
    } else {
      return '₹' + amount.toFixed(2);
    }
  };

  // Format numbers for display
  const formatNumber = (num) => {
    if (num == null || num == undefined || isNaN(num)) return '0';
    return Number(num).toLocaleString();
  };

  // Process new API format
  const processNewApiFormat = (data) => {
    console.log('Processing API data:', data);
    const processedData = {
      arr_time_series: [],
      arr_latest: null,
      cashflow_time_series: [],
      cashflow_latest: null
    };

    // Process ARR data
    if (data.arr) {
      const timestamps = Object.keys(data.arr).sort();
      console.log('ARR timestamps:', timestamps);
      
      processedData.arr_time_series = timestamps.map(timestamp => {
        const timestampData = data.arr[timestamp];
        const organizations = timestampData.organizations;
        const summary = timestampData.summary;
        console.log(`Processing timestamp ${timestamp}:`, {
          summary,
          organization_count: summary?summary.organization_count: 0
        });
        
        return {
          timestamp: timestamp,
          date: timestamp,
          current_arr: summary.total_arr,
          subscription_arr: summary.total_subscription_arr,
          panel_arr: summary.total_panel_arr,
          organizations: organizations,
          organization_count: Number(summary.organization_count) || 0
        };
      });
      
      // Set latest ARR data
      if (processedData.arr_time_series.length > 0) {
        processedData.arr_latest = processedData.arr_time_series[processedData.arr_time_series.length - 1];
        console.log('Latest ARR data:', processedData.arr_latest);
        // Set selected date to latest if not already set
        if (!selectedDate) {
          setSelectedDate(processedData.arr_latest.timestamp);
        }
      }
    }

    // Process Cashflow data
    if (data.cashflow) {
      const timestamps = Object.keys(data.cashflow).sort();
      
      processedData.cashflow_time_series = timestamps.map(timestamp => {
        const cashflowData = data.cashflow[timestamp];
        
        return {
          timestamp: timestamp,
          date: timestamp,
          generation_date: cashflowData.generation_date,
          total_organizations: cashflowData.total_organizations,
          forecast_months: cashflowData.forecast_months,
          organizations: cashflowData.organizations,
          summary: cashflowData.summary,
          monthly_forecast_totals: cashflowData.summary.monthly_forecast_totals,
          monthly_renewal_adjusted_totals: cashflowData.summary.monthly_renewal_adjusted_totals
        };
      });
      
      // Set latest cashflow data
      if (processedData.cashflow_time_series.length > 0) {
        processedData.cashflow_latest = processedData.cashflow_time_series[processedData.cashflow_time_series.length - 1];
        // Set selected cashflow date to latest if not already set
        if (!selectedCashflowDate) {
          setSelectedCashflowDate(processedData.cashflow_latest.timestamp);
        }
      }
    }

    return processedData;
  };

  // Generate cash flow forecast
  const generateCashFlowForecast = async () => {
    try {
      setGeneratingCashflow(true);
      if (set_page_loader) set_page_loader(true, "Generating cash flow forecast...");
      
      const response = await general_fetch({
        url: 'csp/generate_cash_flow_forecast',
        body: {}
      });

      if (response) {
        toastr.success('Cash flow forecast generated successfully');
        // Refresh the dashboard data to get updated forecasts
        await fetchDashboardData();
      } else {
        throw new Error('Failed to generate forecast');
      }
    } catch (error) {
      console.error('Error generating cash flow forecast:', error);
      toastr.error('Failed to generate cash flow forecast');
    } finally {
      setGeneratingCashflow(false);
      if (set_page_loader) set_page_loader(false);
    }
  };

  // Download cashflow projection CSV (latest available or selected date)
  const downloadCashflowCSV = async () => {
    try {
      // Determine which date to download
      const dateToDownload = selectedCashflowDate || (dashboardData && dashboardData.cashflow_latest && dashboardData.cashflow_latest.timestamp);
      const loadingMessage = selectedCashflowDate ? "Downloading CSV for selected date..." : "Downloading latest CSV...";
      
      if (set_page_loader) set_page_loader(true, loadingMessage);
      
      // Use direct fetch instead of general_fetch since this returns CSV text, not JSON
      const response = await fetch(global.config.server_path + 'csp/download_latest_cashflow_forecast', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          date: dateToDownload
        })
      });

      console.log('CSV Download Response:', response);

      if (response.ok) {
        // Get the CSV text directly
        const csvData = await response.text();
        
        if (csvData && csvData.trim()) {
          // Create a blob from the CSV data with proper BOM for Excel compatibility
          const BOM = '\uFEFF';
          const blob = new Blob([BOM + csvData], { type: 'text/csv;charset=utf-8' });
          const url = window.URL.createObjectURL(blob);
          
          // Format the filename with the date
          const filenameDatePart = dateToDownload || new Date().toISOString().split('T')[0];
          const filename = `cashflow_projection_${filenameDatePart}.csv`;
          
          // Create a temporary link and trigger download
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          
          // Clean up
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          const successMessage = selectedCashflowDate ? 'CSV for selected date downloaded successfully' : 'Latest CSV downloaded successfully';
          toastr.success(successMessage);
        } else {
          toastr.error('CSV data is empty. Please generate a new forecast first.');
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toastr.error('Failed to download CSV. Please try generating a new forecast first.');
    } finally {
      if (set_page_loader) set_page_loader(false);
    }
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      if (set_page_loader) set_page_loader(true, "Fetching financial data...");
      
      const body = {
        type: "both",
        days_back: daysBack
      };

      const response = await general_fetch({
        url: 'csp/get_dashboard_data',
        body
      });

      // Console.log the API response for debugging
      console.log('Dashboard API Response:', response);

      // Process the new API format
      if (response) {
        const processedData = processNewApiFormat(response);
        setDashboardData(processedData);
        toastr.success('Financial data loaded successfully');
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toastr.error('Failed to load financial data');
    } finally {
      setLoading(false);
      if (set_page_loader) set_page_loader(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, [daysBack]);

  // Prepare ARR chart data
  const getArrChartData = () => {
    if (!dashboardData || !dashboardData.arr_time_series || dashboardData.arr_time_series.length === 0) return null;

    const sortedData = dashboardData.arr_time_series.sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );

    return {
      labels: sortedData.map(item => 
        new Date(item.date).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        })
      ),
      datasets: [
        {
          label: 'Total ARR',
          data: sortedData.map(item => item.current_arr),
          borderColor: '#5e72e4',
          backgroundColor: 'rgba(94, 114, 228, 0.1)',
          tension: 0.4,
          fill: false,
          pointBackgroundColor: '#5e72e4',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
        },
        {
          label: 'Subscription ARR',
          data: sortedData.map(item => item.subscription_arr || 0),
          borderColor: '#2dce89',
          backgroundColor: 'rgba(45, 206, 137, 0.1)',
          tension: 0.4,
          fill: false,
          pointBackgroundColor: '#2dce89',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
        },
        {
          label: 'Panel ARR',
          data: sortedData.map(item => item.panel_arr || 0),
          borderColor: '#fb6340',
          backgroundColor: 'rgba(251, 99, 64, 0.1)',
          tension: 0.4,
          fill: false,
          pointBackgroundColor: '#fb6340',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
        }
      ]
    };
  };

  // Prepare Cash Flow chart data
  const getCashflowChartData = () => {
    // Use selected cashflow data if available, otherwise fall back to latest
    const selectedCashflow = getSelectedCashflowData();
    const cashflowData = selectedCashflow || (dashboardData && dashboardData.cashflow_latest);
    
    if (!cashflowData) {
      // Fallback to ARR data if no cashflow data
      if (!dashboardData || !dashboardData.arr_time_series || dashboardData.arr_time_series.length === 0) return null;

      const sortedData = dashboardData.arr_time_series.sort((a, b) => 
        new Date(a.date) - new Date(b.date)
      );

      return {
        labels: sortedData.map(item => 
          new Date(item.date).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric' 
          })
        ),
        datasets: [
          {
            label: 'ARR Based Projection',
            data: sortedData.map(item => item.current_arr),
            borderColor: '#11cdef',
            backgroundColor: 'rgba(17, 205, 239, 0.1)',
            tension: 0.4,
            fill: false,
            pointBackgroundColor: '#11cdef',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
          }
        ]
      };
    }

    // Use cashflow data (either selected or latest)
    return {
      labels: cashflowData.forecast_months,
      datasets: [
        {
          label: 'Nominal Cashflow',
          data: cashflowData.monthly_forecast_totals,
          borderColor: '#5e72e4',
          backgroundColor: 'rgba(94, 114, 228, 0.1)',
          tension: 0.4,
          fill: false,
          pointBackgroundColor: '#5e72e4',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
        },
        {
          label: 'Expected Cashflow',
          data: cashflowData.monthly_renewal_adjusted_totals,
          borderColor: '#2dce89',
          backgroundColor: 'rgba(45, 206, 137, 0.1)',
          tension: 0.4,
          fill: false,
          pointBackgroundColor: '#2dce89',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
        }
      ]
    };
  };

  // Get organization data for selected date
  const getSelectedDateData = () => {
    if (!dashboardData || !dashboardData.arr_time_series || !selectedDate) return null;
    
    const dateData = dashboardData.arr_time_series.find(item => item.timestamp === selectedDate);
    return dateData;
  };

  // Get cashflow data for selected date
  const getSelectedCashflowData = () => {
    if (!dashboardData || !dashboardData.cashflow_time_series || !selectedCashflowDate) return null;
    
    const cashflowData = dashboardData.cashflow_time_series.find(item => item.timestamp === selectedCashflowDate);
    return cashflowData;
  };

  // Filter organizations based on search term
  const getFilteredOrganizations = (organizations) => {
    if (!orgSearchTerm) return Object.values(organizations);
    
    return Object.values(organizations).filter(org => 
      org.name.toLowerCase().includes(orgSearchTerm.toLowerCase()) ||
      org.plan.toLowerCase().includes(orgSearchTerm.toLowerCase()) ||
      org.market.toLowerCase().includes(orgSearchTerm.toLowerCase())
    );
  };

  // Get monthly forecast chart data
  const getMonthlyForecastData = () => {
    // Use selected cashflow data if available, otherwise fall back to latest
    const selectedCashflow = getSelectedCashflowData();
    const cashflowData = selectedCashflow || (dashboardData && dashboardData.cashflow_latest);
    
    if (!cashflowData) return null;
    
    return {
      labels: cashflowData.forecast_months,
      datasets: [
        {
          label: 'Nominal Cashflow',
          data: cashflowData.monthly_forecast_totals,
          backgroundColor: 'rgba(94, 114, 228, 0.8)',
          borderColor: '#5e72e4',
          borderWidth: 1,
        },
        {
          label: 'Expected Cashflow',
          data: cashflowData.monthly_renewal_adjusted_totals,
          backgroundColor: 'rgba(45, 206, 137, 0.8)',
          borderColor: '#2dce89',
          borderWidth: 1,
        }
      ]
    };
  };

  // Calculate total expected cashflow (sum of all months)
  const getTotalExpectedCashflow = () => {
    const selectedCashflow = getSelectedCashflowData();
    const cashflowData = selectedCashflow || (dashboardData && dashboardData.cashflow_latest);
    
    if (!cashflowData || !cashflowData.monthly_renewal_adjusted_totals) return 0;
    
    return cashflowData.monthly_renewal_adjusted_totals.reduce((total, monthlyValue) => total + monthlyValue, 0);
  };

  const getCashflowSummary = () => {
    const selectedCashflow = getSelectedCashflowData();
    const cashflowData = selectedCashflow || (dashboardData && dashboardData.cashflow_latest);
    if (!cashflowData) return null;
    return cashflowData.summary;
  };

  // Check if ARR data has very different scales that need separate charts
  const shouldSplitArrCharts = () => {
    if (!dashboardData || !dashboardData.arr_time_series || dashboardData.arr_time_series.length === 0) return false;
    
    // Always split charts to show Total ARR and Panel ARR separately for better readability
    return true;
  };

  // Get ARR chart data for total ARR only
  const getTotalArrChartData = () => {
    if (!dashboardData || !dashboardData.arr_time_series || dashboardData.arr_time_series.length === 0) return null;

    const sortedData = dashboardData.arr_time_series.sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );

    return {
      labels: sortedData.map(item => 
        new Date(item.date).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        })
      ),
      datasets: [
        {
          label: 'Total ARR',
          data: sortedData.map(item => item.current_arr),
          borderColor: '#5e72e4',
          backgroundColor: 'rgba(94, 114, 228, 0.1)',
          tension: 0.4,
          fill: true,
          pointBackgroundColor: '#5e72e4',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
        }
      ]
    };
  };

  // Get ARR chart data for components only
  const getArrComponentsChartData = () => {
    if (!dashboardData || !dashboardData.arr_time_series || dashboardData.arr_time_series.length === 0) return null;

    const sortedData = dashboardData.arr_time_series.sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );

    return {
      labels: sortedData.map(item => 
        new Date(item.date).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        })
      ),
      datasets: [
        {
          label: 'Panel ARR',
          data: sortedData.map(item => item.panel_arr || 0),
          borderColor: '#fb6340',
          backgroundColor: 'rgba(251, 99, 64, 0.1)',
          tension: 0.4,
          fill: true,
          pointBackgroundColor: '#fb6340',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
        }
      ]
    };
  };

  const styles = {
    header_row: {
      marginLeft: 0,
      marginRight: 0,
      overflowX: "auto",
    },
    stat_card: {
      backgroundColor: '#ffffff',
      border: '1px solid #e9ecef',
      borderRadius: '8px',
      padding: '10px',
      textAlign: 'center',
      height: '100%',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
    },
    compact_stat_card: {
      backgroundColor: '#ffffff',
      border: '1px solid #e9ecef',
      borderRadius: '8px',
      padding: '14px',
      textAlign: 'center',
      height: '100%'
    },
    chart_container: {
      position: 'relative',
      height: '280px',
      width: '100%',
      padding: '6px'
    },
    compact_chart_container: {
      position: 'relative',
      height: '400px',
      width: '100%',
      padding: '8px',
      paddingLeft: '14px',
      paddingRight: '14px'
    },
    large_chart_container: {
      position: 'relative',
      height: '380px',
      width: '100%',
      padding: '10px'
    },
    search_input: {
      borderRadius: '20px',
      border: '1px solid #dee2e6',
      paddingLeft: '40px'
    }
  };

  const currentArr = dashboardData && dashboardData.arr_latest ? dashboardData.arr_latest.current_arr : null;
  const selectedDateData = getSelectedDateData();

  return (
    <>
      {/* <Header /> */}
      <Container className="mt-2" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0">
                {/* <Row style={styles.header_row}> */}
                  
                  {/* <Col xs="4" className="text-right">
                    <div className="flex_property" style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                      <span className="mr-2" style={{ fontSize: '14px' }}>Time:</span>
                      <DatePicker.RangePicker
                        onChange={(dates) => {
                          if (dates && dates.length === 2) {
                            const daysDiff = dayjs(dates[1]).diff(dayjs(dates[0]), 'day');
                            setDaysBack(Math.min(daysDiff, 365));
                          }
                        }}
                        format="YYYY-MM-DD"
                        placeholder={['Start Date', 'End Date']}
                      />
                      <Button
                        color="primary"
                        size="sm"
                        className="ml-2"
                        onClick={fetchDashboardData}
                        disabled={loading}
                      >
                        Refresh
                      </Button>
                    </div>
                  </Col> */}
                {/* </Row> */}
                <Row className="mt-0">
                    <Col xs="8">
                        <Nav pills>
                            <NavItem>
                            <NavLink
                                href="#"
                                active={activeTab === 'overview'}
                                onClick={() => setActiveTab('overview')}
                            >
                                <b>Overview</b>
                            </NavLink>
                            </NavItem>
                            <NavItem>
                            <NavLink
                                href="#"
                                active={activeTab === 'arr'}
                                onClick={() => setActiveTab('arr')}
                            >
                                <b>ARR</b>
                            </NavLink>
                            </NavItem>
                            <NavItem>
                            <NavLink
                                href="#"
                                active={activeTab === 'cashflow'}
                                onClick={() => setActiveTab('cashflow')}
                            >
                                <b>Cash Flow</b>
                            </NavLink>
                            </NavItem>
                        </Nav>
                    </Col>
                    {/* <Col xs="4" className="text-right d-flex align-items-center justify-content-end">
                        <Button
                            color="outline-primary"
                            size="sm"
                            onClick={() => setChatbotVisible(true)}
                            className="d-flex align-items-center"
                        >
                            <i className="fas fa-robot mr-1"></i>
                            <small>Ask AI Assistant</small>
                        </Button>
                    </Col> */}
                </Row>
              </CardHeader>

              <CardBody style={{ paddingTop: 0}}>
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div>
                    <Row className="mb-3">
                      <Col lg="6" className="mb-3">
                        {/* ARR Overview Card */}
                        <div style={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e9ecef',
                          borderRadius: '12px',
                          padding: '20px',
                          height: '100%',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
                        }}>
                          <div className="d-flex align-items-center mb-3">
                            <i className="fas fa-chart-line text-primary fa-lg mr-2"></i>
                            <h5 className="text-primary mb-0 font-weight-bold">ARR Overview</h5>
                          </div>
                          <Row>
                            <Col xs="4">
                              <div className="text-center">
                                <h4 className="mb-1 font-weight-bold text-primary">
                                  {currentArr ? formatCurrency(currentArr) : 'Loading...'}
                                </h4>
                                <small className="text-muted">Total ARR</small>
                              </div>
                            </Col>
                            <Col xs="4">
                              <div className="text-center">
                                <h4 className="mb-1 font-weight-bold text-primary">
                                  {dashboardData && dashboardData.arr_latest ? 
                                    formatCurrency(dashboardData.arr_latest.subscription_arr || 0) : 
                                    'Loading...'
                                  }
                                </h4>
                                <small className="text-muted">Subscription ARR</small>
                              </div>
                            </Col>
                            <Col xs="4">
                              <div className="text-center">
                                <h4 className="mb-1 font-weight-bold text-primary">
                                  {dashboardData && dashboardData.arr_latest ? 
                                    formatCurrency(dashboardData.arr_latest.panel_arr || 0) : 
                                    'Loading...'
                                  }
                                </h4>
                                <small className="text-muted">Panel ARR</small>
                              </div>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs="12">
                              <div className="mt-3 text-center">
                                {dashboardData && dashboardData.arr_latest && (
                                <small className="text-muted">
                                  <span className="text-primary text-bold">{dashboardData.arr_latest.organization_count}</span> Active Organizations
                                  {dashboardData && dashboardData.arr_latest && dashboardData.arr_latest.date ? 
                                    ` (as of ${new Date(dashboardData.arr_latest.date).toLocaleDateString()})` : 
                                    ''
                                  }
                                </small>
                                )} 
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </Col>
                      <Col lg="6" className="mb-3">
                        {/* Cash Flow Projection Card */}
                        <div style={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e9ecef',
                          borderRadius: '12px',
                          padding: '20px',
                          height: '100%',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
                        }}>
                          <div className="d-flex align-items-center mb-3">
                            <i className="fas fa-chart-area text-primary fa-lg mr-2"></i>
                            <h5 className="text-primary mb-0 font-weight-bold">Cash Flow Projection</h5>
                          </div>
                          
                          {getCashflowSummary() ? (
                            <div>
                              <Row className="mb-3">
                                <Col xs="6">
                                  <div className="text-center">
                                    <h4 className="mb-1 font-weight-bold text-danger">
                                      {getCashflowSummary().organizations_at_risk}
                                    </h4>
                                    <small className="text-muted">Clients with renewal probability &lt; 50%</small>
                                  </div>
                                </Col>
                                <Col xs="6">
                                  <div className="text-center">
                                    <h4 className="mb-1 font-weight-bold text-primary">
                                      {getCashflowSummary().average_renewal_probability.toFixed(1)}%
                                    </h4>
                                    <small className="text-muted">Avg. renewal probability</small>
                                  </div>
                                </Col>
                              </Row>
                              {/* <Row className="mb-3">
                                <Col xs="12">
                                  <div className="text-center">
                                    <small className="text-muted" style={{ fontSize: '11px' }}>
                                      Generated: {new Date(dashboardData.cashflow_latest.generation_date).toLocaleDateString()}
                                    </small>
                                  </div>
                                </Col>
                              </Row> */}
                              <Row>
                                <Col xs="4">
                                  <Button
                                    color="primary"
                                    onClick={() => setActiveTab('cashflow')}
                                    size="sm"
                                    block
                                  >
                                    <i className="fas fa-chart-line mr-1"></i>
                                    <small>View Details</small>
                                  </Button>
                                </Col>
                                <Col xs="4">
                                  <Button
                                    color="primary"
                                    onClick={downloadCashflowCSV}
                                    size="sm"
                                    block
                                    outline
                                  >
                                    <i className="fas fa-download mr-1"></i>
                                    <small>Download CSV</small>
                                  </Button>
                                </Col>
                                <Col xs="4">
                                  <Button
                                    color="primary"
                                    onClick={generateCashFlowForecast}
                                    disabled={generatingCashflow}
                                    size="sm"
                                    block
                                    outline
                                  >
                                    <i className={generatingCashflow ? "spinner-border spinner-border-sm mr-1" : "fas fa-redo mr-1"}></i>
                                    <small>{generatingCashflow ? "Regenerating..." : "Regenerate"}</small>
                                  </Button>
                                </Col>
                              </Row>
                            </div>
                          ) : (
                            <div>
                              <div className="text-center mb-3">
                                <Button
                                  color="primary"
                                  onClick={generateCashFlowForecast}
                                  disabled={generatingCashflow}
                                  size="sm"
                                  block
                                >
                                  {generatingCashflow ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm mr-1" role="status"></span>
                                      <small>Generating...</small>
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-play mr-1"></i>
                                      <small>Generate Forecast</small>
                                    </>
                                  )}
                                </Button>
                              </div>
                              <small className="text-muted text-center d-block">
                                Generate cash flow projections based on current ARR data
                              </small>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    {/* Quick Charts */}
                    <Row className="mt-3">
                      <Col xs="12">
                        <Card className="shadow-sm">
                          <CardHeader style={{ background: 'linear-gradient(135deg, #5e72e4 0%, #825ee4 100%)' }} className="text-white">
                            <Row className="align-items-center">
                              <Col>
                                <h4 className="mb-0 text-white">
                                  <i className="fas fa-chart-area mr-2"></i>
                                  ARR Trend Overview
                                </h4>
                              </Col>
                              <Col xs="auto">
                                <small className="text-white opacity-8">Last {daysBack} days</small>
                              </Col>
                            </Row>
                          </CardHeader>
                          <CardBody style={styles.compact_chart_container}>
                            {shouldSplitArrCharts() ? (
                              <div>
                                {/* Total ARR Chart */}
                                <div className="mb-3">
                                  <h6 className="text-muted mb-2 font-weight-bold">
                                    <i className="fas fa-chart-line text-primary mr-1"></i>
                                    Total ARR
                                  </h6>
                                  <div style={{height: '140px'}}>
                                    <Line options={arrChartOptions} data={getTotalArrChartData()} />
                                  </div>
                                </div>
                                <hr/>
                                {/* Panel ARR Chart */}
                                <div>
                                  <h6 className="text-muted mb-2 font-weight-bold">
                                    <i className="fas fa-layer-group text-info mr-1"></i>
                                    Panel ARR
                                  </h6>
                                  <div style={{height: '140px'}}>
                                    <Line options={arrChartOptions} data={getArrComponentsChartData()} />
                                  </div>
                                </div>
                              </div>
                            ) : getArrChartData() ? (
                              <Line options={arrChartOptions} data={getArrChartData()} />
                            ) : (
                              <div className="text-center d-flex align-items-center justify-content-center h-100">
                                <div className="spinner-border text-primary" role="status">
                                  <span className="sr-only">Loading chart...</span>
                                </div>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                )}

                {/* ARR Analysis Tab */}
                {activeTab === 'arr' && (
                  <div>
                    {/* ARR Breakdown for Selected Date */}
                    {selectedDateData ? (
                      <>
                        <Row className="mb-2">
                          <Col lg="3" md="6" className="mb-3">
                            <div className="d-flex align-items-center justify-content-between p-3" style={{
                              backgroundColor: '#ffffff',
                              color: '#5e72e4',
                              borderRadius: '10px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                              border: '1px solid #e9ecef',
                            }}>
                              <i className="fas fa-chart-line fa-2x text-primary"></i>
                              <div className="text-right">
                                <h6 className="mb-0 text-primary font-weight-bold">Total ARR</h6>
                                <h4 className="mb-0 font-weight-bold text-dark">{formatCurrency(selectedDateData.current_arr)}</h4>
                              </div>
                            </div>
                          </Col>
                          <Col lg="3" md="6" className="mb-3">
                            <div className="d-flex align-items-center justify-content-between p-3" style={{
                              backgroundColor: '#ffffff',
                              color: '#5e72e4',
                              borderRadius: '10px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                              border: '1px solid #e9ecef',
                            }}>
                              <i className="fas fa-sync fa-2x text-primary"></i>
                              <div className="text-right">
                                <h6 className="mb-0 text-primary font-weight-bold">Subscription ARR</h6>
                                <h4 className="mb-0 font-weight-bold text-dark">{formatCurrency(selectedDateData.subscription_arr || 0)}</h4>
                              </div>
                            </div>
                          </Col>
                          <Col lg="3" md="6" className="mb-3">
                            <div className="d-flex align-items-center justify-content-between p-3" style={{
                              backgroundColor: '#ffffff',
                              color: '#5e72e4',
                              borderRadius: '10px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                              border: '1px solid #e9ecef',
                            }}>
                              <i className="fas fa-layer-group fa-2x text-primary"></i>
                              <div className="text-right">
                                <h6 className="mb-0 text-primary font-weight-bold">Panel ARR</h6>
                                <h4 className="mb-0 font-weight-bold text-dark">{formatCurrency(selectedDateData.panel_arr || 0)}</h4>
                              </div>
                            </div>
                          </Col>
                          <Col lg="3" md="6" className="mb-3">
                            <div className="d-flex align-items-center justify-content-between p-3" style={{
                              backgroundColor: '#ffffff',
                              color: '#5e72e4',
                              borderRadius: '10px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                              border: '1px solid #e9ecef',
                            }}>
                              <i className="fas fa-building fa-2x text-primary"></i>
                              <div className="text-right">
                                <h6 className="mb-0 text-primary font-weight-bold">Organizations</h6>
                                <h4 className="mb-0 font-weight-bold text-dark">{Object.keys(selectedDateData.organizations).length}</h4>
                              </div>
                            </div>
                          </Col>
                        </Row>

                        {/* Organization Details Table */}
                        <Row>
                          <Col xs="12">
                            <Card className="shadow-sm">
                              <CardHeader className="bg-primary text-white">
                                <Row className="align-items-center">
                                  <Col>
                                    <h4 className="mb-0 text-white">
                                      <i className="fas fa-building mr-2"></i>
                                      Organizations by ARR
                                    </h4>
                                  </Col>
                                  <Col xs="auto" className="d-flex align-items-center">
                                    {/* Date Selector - Moved to header with reduced prominence */}
                                    <div className="mr-3">
                                      <select 
                                        className="form-control form-control-sm"
                                        value={selectedDate || ''}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        style={{ 
                                          minWidth: '200px',
                                          backgroundColor: 'rgba(255,255,255,0.1)',
                                          border: '1px solid rgba(255,255,255,0.2)',
                                          color: 'white'
                                        }}
                                      >
                                        <option value="" style={{ color: 'black' }}>Select a date...</option>
                                        {dashboardData && dashboardData.arr_time_series && 
                                          dashboardData.arr_time_series
                                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                                            .map((item) => (
                                              <option key={item.timestamp} value={item.timestamp} style={{ color: 'black' }}>
                                                {new Date(item.date).toLocaleDateString('en-US', { 
                                                  month: 'short', 
                                                  day: 'numeric',
                                                  hour: '2-digit',
                                                  minute: '2-digit'
                                                })}
                                              </option>
                                            ))
                                        }
                                      </select>
                                    </div>
                                    {/* Search Input */}
                                    <div className="position-relative">
                                      <i className="fas fa-search position-absolute" style={{
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#adb5bd',
                                        zIndex: 1
                                      }}></i>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Search organizations..."
                                        value={orgSearchTerm}
                                        onChange={(e) => setOrgSearchTerm(e.target.value)}
                                        style={{
                                          ...styles.search_input,
                                          minWidth: '200px'
                                        }}
                                      />
                                    </div>
                                  </Col>
                                </Row>
                              </CardHeader>
                              <CardBody>
                                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                  <table className="table table-sm table-striped">
                                    <thead className="sticky-top">
                                      <tr style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                        <th className="text-white font-weight-bold">
                                          <i className="fas fa-building mr-1"></i>
                                          Organization
                                        </th>
                                        <th className="text-white font-weight-bold">
                                          <i className="fas fa-tag mr-1"></i>
                                          Plan
                                        </th>
                                        <th className="text-white font-weight-bold">
                                          <i className="fas fa-globe mr-1"></i>
                                          Market
                                        </th>
                                        <th className="text-white font-weight-bold">
                                          <i className="fas fa-users mr-1"></i>
                                          Licenses
                                        </th>
                                        <th className="text-white font-weight-bold">
                                          <i className="fas fa-sync mr-1"></i>
                                          Subscription ARR
                                        </th>
                                        <th className="text-white font-weight-bold">
                                          <i className="fas fa-layer-group mr-1"></i>
                                          Panel ARR
                                        </th>
                                        <th className="text-white font-weight-bold">
                                          <i className="fas fa-chart-line mr-1"></i>
                                          Total ARR
                                        </th>
                                        <th className="text-white font-weight-bold">
                                          <i className="fas fa-calendar mr-1"></i>
                                          Created
                                        </th>
                                        <th className="text-white font-weight-bold">
                                          <i className="fas fa-credit-card mr-1"></i>
                                          Last Payment
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {getFilteredOrganizations(selectedDateData.organizations)
                                        .sort((a, b) => (b.total_arr || 0) - (a.total_arr || 0))
                                        .map((org) => (
                                          <tr key={org.id}>
                                            <td>
                                              <div className="d-flex align-items-center">
                                                <i className="fas fa-building text-muted mr-2"></i>
                                                <strong>{org.name}</strong>
                                              </div>
                                            </td>
                                            <td><span className="badge badge-secondary">{org.plan}</span></td>
                                            <td><span className="text-capitalize">{org.market}</span></td>
                                            <td>
                                              <span className="badge badge-light">
                                                <i className="fas fa-users mr-1"></i>
                                                {org.licenses}
                                              </span>
                                            </td>
                                            <td>{formatCurrency(org.subscription_arr || 0)}</td>
                                            <td>{formatCurrency(org.panel_arr || 0)}</td>
                                            <td><strong className="text-primary">{formatCurrency(org.total_arr || 0)}</strong></td>
                                            <td><small>{new Date(org.created_at).toLocaleDateString()}</small></td>
                                            <td><small>{org.last_payment_date ? new Date(org.last_payment_date).toLocaleDateString() : 'N/A'}</small></td>
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                                <div className="mt-3 d-flex justify-content-between align-items-center">
                                  <small className="text-muted">
                                    <i className="fas fa-info-circle mr-1"></i>
                                    Showing {getFilteredOrganizations(selectedDateData.organizations).length} of {Object.keys(selectedDateData.organizations).length} organizations
                                  </small>
                                  {orgSearchTerm && (
                                    <Button
                                      color="link"
                                      size="sm"
                                      onClick={() => setOrgSearchTerm('')}
                                      className="text-primary p-0"
                                    >
                                      <i className="fas fa-times mr-1"></i>
                                      Clear search
                                    </Button>
                                  )}
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </>
                    ) : (
                      <div className="text-center py-5">
                        <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
                        <h5 className="text-muted">No ARR data selected</h5>
                        <p className="text-muted">Please select a date from the dropdown to view ARR breakdown</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Cash Flow Tab */}
                {activeTab === 'cashflow' && (
                  <div>
                    {/* Cashflow Summary Cards */}
                    {getCashflowSummary() ? (
                      <>
                        {/* First row of summary cards */}
                        <Row className="mb-1 align-items-center">
                          <Col lg="2" md="6" className="mb-3">
                            <div className="d-flex align-items-center justify-content-between p-3" style={{
                              backgroundColor: '#ffffff',
                              color: '#5e72e4',
                              borderRadius: '10px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                              border: '1px solid #e9ecef'
                            }}>
                              <i className="fas fa-chart-line fa-2x text-primary"></i>
                              <div className="text-right">
                                <h6 className="mb-0 text-primary font-weight-bold">Total ARR</h6>
                                <h4 className="mb-0 font-weight-bold text-dark">{formatCurrency(getCashflowSummary().total_combined_arr)}</h4>
                              </div>
                            </div>
                          </Col>
                          <Col lg="2" md="6" className="mb-3">
                            <div className="d-flex align-items-center justify-content-between p-3" style={{
                              backgroundColor: '#ffffff',
                              color: '#2dce89',
                              borderRadius: '10px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                              border: '1px solid #e9ecef'
                            }}>
                              <i className="fas fa-coins fa-2x text-primary"></i>
                              <div className="text-right">
                                <h6 className="mb-0 text-primary font-weight-bold">Total Expected Cashflow</h6>
                                <h4 className="mb-0 font-weight-bold text-dark">{formatCurrency(getTotalExpectedCashflow())}</h4>
                              </div>
                            </div>
                          </Col>
                          <Col lg="2" md="6" className="mb-3">
                            <div className="d-flex align-items-center justify-content-between p-3" style={{
                              backgroundColor: '#ffffff',
                              color: '#5e72e4',
                              borderRadius: '10px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                              border: '1px solid #e9ecef'
                            }}>
                              <i className="fas fa-building fa-2x text-primary"></i>
                              <div className="text-right">
                                <h6 className="mb-0 text-primary font-weight-bold">Organizations</h6>
                                <h4 className="mb-0 font-weight-bold text-dark">
                                  {getSelectedCashflowData() ? 
                                    getSelectedCashflowData().total_organizations : 
                                    dashboardData.cashflow_latest.total_organizations
                                  }
                                </h4>
                              </div>
                            </div>
                          </Col>
                          <Col lg="2" md="6" className="mb-3">
                            <div className="d-flex align-items-center justify-content-between p-3" style={{
                              backgroundColor: '#ffffff',
                              color: '#5e72e4',
                              borderRadius: '10px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                              border: '1px solid #e9ecef'
                            }}>
                              <i className="fas fa-percentage fa-2x text-primary"></i>
                              <div className="text-right">
                                <h6 className="mb-0 text-primary font-weight-bold">Avg. renewal probability</h6>
                                <h4 className="mb-0 font-weight-bold text-dark">{getCashflowSummary().average_renewal_probability.toFixed(1)}%</h4>
                              </div>
                            </div>
                          </Col>
                          <Col lg="2" md="6" className="mb-3">
                            <div className="d-flex align-items-center justify-content-between p-3" style={{
                              backgroundColor: '#ffffff',
                              color: '#5e72e4',
                              borderRadius: '10px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                              border: '1px solid #e9ecef'
                            }}>
                              <i className="fas fa-exclamation-triangle fa-2x text-danger"></i>
                              <div className="text-right">
                                <h6 className="mb-0 text-danger font-weight-bold">Orgs with renewal prob. {'< 50%'}</h6>
                                <h4 className="mb-0 font-weight-bold text-dark">{getCashflowSummary().organizations_at_risk}</h4>
                              </div>
                            </div>
                          </Col>
                        </Row>

                        {/* Second row for the at-risk organizations card */}
                        {/* <Row className="mb-1">
                          
                        </Row> */}

                        {/* Forecast Charts */}
                        <Row className="mb-4">
                          <Col xs="12">
                            <Card className="shadow-sm">
                              <CardHeader className="bg-primary text-white">
                                <Row className="align-items-center">
                                  <Col>
                                    <h4 className="mb-0 text-white">
                                      <i className="fas fa-chart-area mr-2"></i>
                                      12-Month Cash Flow Projection
                                      {getSelectedCashflowData() && (
                                        <small className="ml-2 opacity-8">
                                          (as of {new Date(getSelectedCashflowData().generation_date).toLocaleDateString()})
                                        </small>
                                      )}
                                    </h4>
                                  </Col>
                                  <Col xs="auto" className="d-flex align-items-center">
                                    {/* Cashflow Date Selector */}
                                    <div className="mr-3">
                                      <select 
                                        className="form-control form-control-sm"
                                        value={selectedCashflowDate || ''}
                                        onChange={(e) => setSelectedCashflowDate(e.target.value)}
                                        style={{ 
                                          minWidth: '200px',
                                          backgroundColor: 'rgba(255,255,255,0.1)',
                                          border: '1px solid rgba(255,255,255,0.2)',
                                          color: 'white'
                                        }}
                                      >
                                        <option value="" style={{ color: 'black' }}>Select cashflow date...</option>
                                        {dashboardData && dashboardData.cashflow_time_series && 
                                          dashboardData.cashflow_time_series
                                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                                            .map((item) => (
                                              <option key={item.timestamp} value={item.timestamp} style={{ color: 'black' }}>
                                                {new Date(item.generation_date).toLocaleDateString('en-US', { 
                                                  month: 'short', 
                                                  day: 'numeric',
                                                  year: 'numeric',
                                                  hour: '2-digit',
                                                  minute: '2-digit'
                                                })}
                                              </option>
                                            ))
                                        }
                                      </select>
                                    </div>
                                    <div className="mr-3">
                                      <small className="text-white opacity-8">
                                        {getSelectedCashflowData() ? 
                                          `Generated: ${new Date(getSelectedCashflowData().generation_date).toLocaleDateString()}` :
                                          `Generated: ${new Date(dashboardData.cashflow_latest.generation_date).toLocaleDateString()}`
                                        }
                                      </small>
                                    </div>
                                    <Button
                                      color="secondary"
                                      size="sm"
                                      onClick={downloadCashflowCSV}
                                      className="btn-sm"
                                    >
                                      <i className="fas fa-download mr-1"></i>
                                      CSV
                                    </Button>
                                    <Button
                                      color="secondary"
                                      size="sm"
                                      onClick={generateCashFlowForecast}
                                      disabled={generatingCashflow}
                                      className="btn-sm mr-2"
                                    >
                                      {generatingCashflow ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm mr-1" role="status"></span>
                                          Regenerating...
                                        </>
                                      ) : (
                                        <>
                                          <i className="fas fa-redo mr-1"></i>
                                          Regenerate
                                        </>
                                      )}
                                    </Button>
                                  </Col>
                                </Row>
                              </CardHeader>
                              <CardBody style={styles.large_chart_container}>
                                <Line options={cashflowChartOptions} data={getCashflowChartData()} />
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>

                        {/* Monthly Forecast Bar Chart */}
                        <Row className="mb-4">
                          <Col xs="12">
                            <Card className="shadow-sm">
                              <CardHeader className="bg-primary text-white">
                                <h4 className="mb-0 text-white">
                                  <i className="fas fa-chart-bar mr-2"></i>
                                  Monthly Forecast Breakdown
                                </h4>
                              </CardHeader>
                              <CardBody style={styles.large_chart_container}>
                                {getMonthlyForecastData() ? (
                                  <Bar 
                                    options={{
                                      ...cashflowChartOptions,
                                      scales: {
                                        ...cashflowChartOptions.scales,
                                        y: {
                                          ...cashflowChartOptions.scales.y,
                                          beginAtZero: true
                                        }
                                      }
                                    }} 
                                    data={getMonthlyForecastData()} 
                                  />
                                ) : (
                                  <div className="text-center d-flex align-items-center justify-content-center h-100">
                                    <div className="spinner-border text-success" role="status">
                                      <span className="sr-only">Loading chart...</span>
                                    </div>
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </>
                    ) : (
                      <>
                        {/* Generate Cash Flow CTA */}
                        <Row className="mb-4">
                          <Col xs="12">
                            <Card className="shadow-sm border-0">
                              <CardBody className="bg-gradient-primary text-white text-center" style={{ borderRadius: '8px' }}>
                                <div className="py-3">
                                  <i className="fas fa-chart-area fa-3x mb-3 opacity-8"></i>
                                  <h4 className="mb-3 text-white">Cash Flow Projection</h4>
                                  <p className="text-white opacity-8 mb-4">
                                    Generate detailed cash flow forecasts based on current ARR data and historical trends.
                                  </p>
                                  <div className="d-flex justify-content-center">
                                    <Button
                                      color="light"
                                      size="lg"
                                      onClick={generateCashFlowForecast}
                                      disabled={generatingCashflow}
                                      className="px-4 mr-3"
                                    >
                                      {generatingCashflow ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                          Generating Forecast...
                                        </>
                                      ) : (
                                        <>
                                          <i className="fas fa-play mr-2"></i>
                                          Generate Cash Flow Forecast
                                        </>
                                      )}
                                    </Button>
                                    <Button
                                      color="outline-light"
                                      size="lg"
                                      onClick={downloadCashflowCSV}
                                      className="px-4"
                                    >
                                      <i className="fas fa-download mr-2"></i>
                                      Download Latest CSV
                                    </Button>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>

                        {/* Fallback to ARR Chart */}
                        <Row className="mb-4">
                          <Col xs="12">
                            <Card className="shadow-sm">
                              <CardHeader style={{ background: 'linear-gradient(135deg, #11cdef 0%, #1171ef 100%)' }} className="text-white">
                                <Row className="align-items-center">
                                  <Col>
                                    <h4 className="mb-0 text-white">
                                      <i className="fas fa-chart-area mr-2"></i>
                                      Revenue Projection Timeline
                                    </h4>
                                  </Col>
                                  <Col xs="auto">
                                    <small className="text-white opacity-8">Based on historical ARR data</small>
                                  </Col>
                                </Row>
                              </CardHeader>
                              <CardBody style={styles.large_chart_container}>
                                {getCashflowChartData() ? (
                                  <Line options={cashflowChartOptions} data={getCashflowChartData()} />
                                ) : (
                                  <div className="text-center d-flex align-items-center justify-content-center h-100">
                                    <div>
                                      <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
                                      <h5 className="text-muted">No projection data available</h5>
                                      <p className="text-muted">Generate a cash flow forecast to see projections</p>
                                    </div>
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </>
                    )}
                  </div>
                )}

                {loading && (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </Row>
      </Container>

      {/* Financial Chatbot */}
      {/* <FinancialChatbot
        dashboardData={dashboardData}
        isVisible={chatbotVisible}
        onToggle={() => setChatbotVisible(false)}
      /> */}
    </>
  );
};

export default FinancialReports;
